import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, CheckCircle, Mail, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  { value: 'investor', label: 'Investor' },
  { value: 'sponsor', label: 'Project Sponsor' },
  { value: 'bank', label: 'Bank / Lender' },
  { value: 'epc', label: 'EPC Contractor' },
  { value: 'buyer', label: 'Commodity Buyer' },
  { value: 'government', label: 'Government' },
  { value: 'offtaker', label: 'Offtaker' },
  { value: 'utility', label: 'Utility' },
  { value: 'oem', label: 'OEM' },
  { value: 'ipp', label: 'IPP' },
  { value: 'intelligence_provider', label: 'Intelligence Provider' },
  { value: 'consultant', label: 'Consultant' },
  { value: 'investment_banker', label: 'Investment Banker' },
  { value: 'private_equity', label: 'Private Equity' },
  { value: 'legal', label: 'Legal' }
].sort((a, b) => a.label.localeCompare(b.label));

const BLOCKED_DOMAINS = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com',
  'aol.com', 'mail.com', 'protonmail.com', 'zoho.com', 'yandex.com',
  'live.com', 'msn.com', 'me.com', 'gmx.com', 'inbox.com',
  'yahoo.co.uk', 'hotmail.co.uk', 'googlemail.com', 'qq.com', '163.com',
  'rediffmail.com', 'fastmail.com', 'tutanota.com', 'mailinator.com',
];

export function RegisterPage() {
  const [formData, setFormData] = useState({
    email: '', password: '', full_name: '', role: 'investor',
    organization: '', country: '', phone: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [registrationResult, setRegistrationResult] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const navigate = useNavigate();

  const validate = () => {
    const errs = {};
    if (!formData.full_name.trim()) errs.full_name = 'Full name is required';
    if (!formData.email.trim()) {
      errs.email = 'Email is required';
    } else {
      const domain = formData.email.split('@')[1]?.toLowerCase();
      if (domain && BLOCKED_DOMAINS.includes(domain)) {
        errs.email = 'Please use a professional work email address.';
      }
    }
    if (!formData.organization.trim()) errs.organization = 'Organization is required';
    if (!formData.password || formData.password.length < 6) errs.password = 'Password must be at least 6 characters';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register`, formData);
      setRegistrationResult(res.data);
      toast.success('Registration submitted!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmail = async () => {
    if (!registrationResult?.verification_token) return;
    setVerifying(true);
    try {
      await axios.get(`${API}/auth/verify-email/${registrationResult.verification_token}`);
      setEmailVerified(true);
      toast.success('Email verified successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const fieldClass = (field) => errors[field] ? 'border-red-500 focus-visible:ring-red-500' : '';

  // Post-registration: verification + approval flow
  if (registrationResult) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            {emailVerified ? (
              <>
                <div className="mx-auto w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-2">
                  <ShieldCheck size={24} className="text-green-600" />
                </div>
                <CardTitle className="text-xl">Email Verified</CardTitle>
                <CardDescription>Your account is now pending admin review</CardDescription>
              </>
            ) : (
              <>
                <div className="mx-auto w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-2">
                  <Mail size={24} className="text-blue-600" />
                </div>
                <CardTitle className="text-xl">Verify Your Email</CardTitle>
                <CardDescription>One more step to complete your registration</CardDescription>
              </>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {!emailVerified ? (
              <>
                <div className="p-4 bg-muted/50 rounded-lg text-sm text-center space-y-2">
                  <p>A verification link has been sent to:</p>
                  <p className="font-semibold">{formData.email}</p>
                  <p className="text-muted-foreground text-xs">Click the button below to confirm your email address</p>
                </div>
                <Button onClick={handleVerifyEmail} disabled={verifying} className="w-full" data-testid="verify-email-btn">
                  <CheckCircle size={14} className="mr-2" />
                  {verifying ? 'Verifying...' : 'Verify My Email'}
                </Button>
              </>
            ) : (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-sm space-y-3">
                <div className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
                  <p>Your email has been verified successfully.</p>
                </div>
                <div className="flex items-start gap-2">
                  <ShieldCheck size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                  <p>An INMX administrator will now review your registration and approve your access. You will be notified once your account is active.</p>
                </div>
              </div>
            )}

            <div className="pt-2 border-t text-center">
              <Button variant="ghost" size="sm" onClick={() => navigate('/login')} data-testid="go-to-login">
                <ArrowLeft size={14} className="mr-1" /> Go to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle className="text-2xl">Create Account</CardTitle>
          <CardDescription>Join the INMX B2B Platform. Registration requires email verification and admin approval.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name *</Label>
                <Input id="full_name" value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} className={fieldClass('full_name')} data-testid="fullname-input" />
                {errors.full_name && <p className="text-xs text-red-500">{errors.full_name}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Work Email *</Label>
                <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className={fieldClass('email')} data-testid="email-input" />
                {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="organization">Organization *</Label>
                <Input id="organization" value={formData.organization} onChange={(e) => setFormData({ ...formData, organization: e.target.value })} className={fieldClass('organization')} data-testid="organization-input" />
                {errors.organization && <p className="text-xs text-red-500">{errors.organization}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Role *</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                  <SelectTrigger data-testid="role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Input id="country" value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} data-testid="country-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input id="phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} data-testid="phone-input" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password *</Label>
              <Input id="password" type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className={fieldClass('password')} data-testid="password-input" />
              {errors.password && <p className="text-xs text-red-500">{errors.password}</p>}
            </div>

            <div className="p-3 bg-muted/50 rounded-lg text-xs text-muted-foreground">
              <p>By registering, your account will undergo a two-step process:</p>
              <ol className="list-decimal ml-4 mt-1 space-y-0.5">
                <li>Email verification — confirm your work email address</li>
                <li>Admin approval — an INMX administrator will review and approve your access</li>
              </ol>
            </div>

            <Button type="submit" className="w-full" disabled={loading} data-testid="register-button">
              {loading ? 'Submitting...' : 'Submit Registration'}
            </Button>
          </form>
          <div className="mt-4 flex items-center justify-between text-sm">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-to-home">
              <ArrowLeft size={14} className="mr-1" /> Homepage
            </Button>
            <span>
              <span className="text-muted-foreground">Already have an account? </span>
              <Link to="/login" className="text-accent hover:underline" data-testid="login-link">Sign in</Link>
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
